import type { InfiniteData, Query, QueryClient } from '@tanstack/react-query';
import { detalharMotivo } from './motivo-da-busca';
import type { TicketDetails, TicketSummary, TicketsListResponse } from './types';

/**
 * Instrumentação da inbox — Fase 0 do plano de redução da tempestade de
 * requisições (`_plans/cozy-floating-charm.md`).
 *
 * Objetivo: atribuir, com evidência objetiva, cada refetch de `['tickets']`
 * (cada `invalidate` da lista dispara 3 GETs — active/pending/closed) a um
 * evento/origem, pra medir o baseline e provar o ganho de cada incremento.
 *
 * NÃO muda comportamento: quando o flag está desligado (default), os wrappers
 * só repassam a chamada. Ligar em runtime no console do navegador:
 *
 *   localStorage.setItem('cp_inbox_debug','1')   // liga
 *   localStorage.removeItem('cp_inbox_debug')    // desliga
 *   window.__inboxStats                          // lê os contadores acumulados
 *
 * Os contadores acumulam SEMPRE (mesmo com o flag off) — custo é um `++` num
 * objeto em memória; o flag só controla o `console.debug`. Assim dá pra abrir o
 * console a qualquer momento e ler `__inboxStats` sem ter religado antes.
 */

export interface InboxStats {
  /** eventName (socket) → nº de vezes que um handler tocou o cache por causa dele */
  byEvent: Record<string, number>;
  /** origin (nome do handler) → nº de patches que MUDARAM o cache */
  patches: Record<string, number>;
  /** origin → nº de patches que não acharam o ticket (no-op / onMissing) */
  skips: Record<string, number>;
  /** origin → nº de invalidates disparados (cada um = 3 GETs de lista) */
  invalidates: Record<string, number>;
}

declare global {
  interface Window {
    __inboxStats?: InboxStats;
  }
}

function stats(): InboxStats {
  if (typeof window === 'undefined') {
    // SSR/worker — objeto descartável, ninguém lê.
    return { byEvent: {}, patches: {}, skips: {}, invalidates: {} };
  }
  if (!window.__inboxStats) {
    window.__inboxStats = { byEvent: {}, patches: {}, skips: {}, invalidates: {} };
  }
  return window.__inboxStats;
}

function bump(bucket: Record<string, number>, key: string | undefined): void {
  if (!key) return;
  bucket[key] = (bucket[key] ?? 0) + 1;
}

/** Flag de log em runtime (não afeta a contagem, que é sempre acumulada). */
export function isInboxDebug(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('cp_inbox_debug') === '1';
  } catch {
    return false;
  }
}

function log(kind: string, origin: string, event?: string): void {
  if (!isInboxDebug()) return;
  console.debug(`[inbox] ${kind}`, event ? `${origin} ← ${event}` : origin);
}

/**
 * Wrapper instrumentado de `qc.invalidateQueries`. Uso 1:1 no lugar do
 * `qc.invalidateQueries({ queryKey })` — sem alterar a chave. Registra a origem
 * (nome do handler) e, quando disponível, o nome do evento de socket que
 * disparou (correção #1 da revisão: `onTicketChanged` atende 5 eventos).
 */
export function debugInvalidate(
  qc: QueryClient,
  queryKey: readonly unknown[],
  origin: string,
  event?: string,
  /** Restringe a quais caches (dentro do `queryKey`) a invalidação vale. */
  predicate?: (query: Query) => boolean,
): Promise<void> {
  const s = stats();
  bump(s.invalidates, origin);
  bump(s.byEvent, event);
  log('invalidate', origin, event);
  detalharMotivo(origin);
  return qc.invalidateQueries({ queryKey: [...queryKey], ...(predicate && { predicate }) });
}

interface PatchTicketOpts {
  /** Prefixo do queryKey a casar (setQueriesData faz match por prefixo).
   *  Default `['tickets']` (todas as listas). `onInboundInbox` usa
   *  `['tickets', status]` pra mexer só na aba do evento. */
  keyPrefix?: readonly unknown[];
  /** Move o ticket pro topo da 1ª página (comportamento do inbound). Default false. */
  reorder?: boolean;
  /** Quando o ticket não está em nenhuma lista carregada:
   *  - 'skip' (default): no-op (o card aparece no próximo refetch natural)
   *  - 'invalidate': invalida o `keyPrefix` FORA do updater (traz o ticket novo) */
  onMissing?: 'skip' | 'invalidate';
  /** Nome do handler, pra atribuição no `__inboxStats`. */
  origin: string;
  /** Nome do evento de socket, pra `byEvent`. */
  event?: string;
}

/**
 * Abstração única dos patches otimistas na lista de tickets do `useInfiniteQuery`.
 *
 * Substitui a mecânica repetida ("achar o ticket em todas as pages → aplicar
 * updater → opcionalmente reordenar pro topo → tratar ausência") que estava
 * copiada em `onInboundInbox`, `onTicketRead`, `onTransferredCleared` e
 * `onChatbotStarted`.
 *
 * Convenção do `updater`: retornar a **mesma referência** (`(t) => t`) sinaliza
 * "sem mudança" → a página não é recriada (evita re-render à toa), replicando o
 * `if (t.unread_count === 0) return p` dos handlers originais. Isso NÃO se
 * aplica quando `reorder: true` (o inbound sempre reordena).
 *
 * Registra em `__inboxStats.patches[origin]` (mudou algo) ou `skips[origin]`
 * (nada mudou / ticket ausente), e chama `debugInvalidate` quando
 * `onMissing: 'invalidate'` e o ticket não estava em nenhuma lista.
 */
export function patchTicket(
  qc: QueryClient,
  ticketId: number,
  updater: (t: TicketSummary) => TicketSummary,
  opts: PatchTicketOpts,
): void {
  const keyPrefix = opts.keyPrefix ?? ['tickets'];
  const reorderPedido = opts.reorder ?? false;
  const onMissing = opts.onMissing ?? 'skip';
  let missingAny = false;
  let changedAny = false;

  // Lista por lista: o "subir pro topo" (reorder) só vale na ordem por
  // atividade — na ordem de CHEGADA a posição é congelada (opção da lista).
  const aplicar = (
    prev: InfiniteData<TicketsListResponse> | undefined,
    reorder: boolean,
  ): InfiniteData<TicketsListResponse> | undefined => {
    if (!prev) {
      // Query existe no cache mas sem dado carregado — trata como ausente.
      missingAny = true;
      return prev;
    }

    if (!reorder) {
      // Sem reorder: aplica o updater em TODA page que contenha o ticket —
      // fidelidade aos handlers originais (onTicketRead etc. iteravam todas as
      // pages via findIndex por página; duplicata cross-page é possível com
      // cursor pagination). Achado da revisão adversarial da Fase 0.
      let found = false;
      let changed = false;
      const pages = prev.pages.map((p) => {
        if (!p) return p;
        const idx = p.tickets.findIndex((t) => t.id === ticketId);
        if (idx === -1) return p;
        const ticket = p.tickets[idx];
        if (!ticket) return p;
        found = true;
        const updated = updater(ticket);
        // Same-ref = no-op nesta page (convenção do updater).
        if (updated === ticket) return p;
        changed = true;
        const tickets = [...p.tickets];
        tickets[idx] = updated;
        return { ...p, tickets };
      });
      if (!found) {
        missingAny = true;
        return prev;
      }
      if (!changed) return prev;
      changedAny = true;
      return { ...prev, pages };
    }

    // reorder: acha a PRIMEIRA page com o ticket (igual ao inbound original),
    // remove da page de origem e prepende na 1ª page (topo da lista).
    // Total não muda — só reordenação.
    let foundPageIdx = -1;
    let foundIdx = -1;
    for (let i = 0; i < prev.pages.length; i++) {
      const page = prev.pages[i];
      if (!page) continue;
      const idx = page.tickets.findIndex((t) => t.id === ticketId);
      if (idx !== -1) {
        foundPageIdx = i;
        foundIdx = idx;
        break;
      }
    }
    if (foundPageIdx === -1) {
      missingAny = true;
      return prev;
    }
    const sourcePage = prev.pages[foundPageIdx];
    if (!sourcePage) return prev;
    const ticket = sourcePage.tickets[foundIdx];
    if (!ticket) return prev;
    const updated = updater(ticket);
    const firstPage = prev.pages[0];
    if (!firstPage) return prev;
    changedAny = true;
    const pages = prev.pages.map((p, i) => {
      if (!p) return p;
      if (i === foundPageIdx && i === 0) {
        const without = p.tickets.filter((t) => t.id !== ticketId);
        return { ...p, tickets: [updated, ...without] };
      }
      if (i === foundPageIdx) {
        return { ...p, tickets: p.tickets.filter((t) => t.id !== ticketId) };
      }
      if (i === 0) {
        return { ...p, tickets: [updated, ...p.tickets] };
      }
      return p;
    });
    return { ...prev, pages };
  };
  for (const query of qc.getQueryCache().findAll({ queryKey: [...keyPrefix] })) {
    const ordem = parseListKey(query.queryKey)?.ordem ?? 'atividade';
    qc.setQueryData<InfiniteData<TicketsListResponse>>(query.queryKey, (prev) =>
      aplicar(prev, reorderPedido && ordem === 'atividade'),
    );
  }

  const s = stats();
  // byEvent conta UMA vez por invocação (= por evento de socket tratado). O
  // invalidate interno abaixo NÃO re-bumpa byEvent — senão um `message.received`
  // de ticket fora das listas contaria 2x e inflaria o baseline (achado da
  // revisão adversarial).
  bump(s.byEvent, opts.event);
  if (changedAny) {
    bump(s.patches, opts.origin);
    log('patch', opts.origin, opts.event);
  } else {
    bump(s.skips, opts.origin);
    log('skip', opts.origin, opts.event);
  }

  // Invalida FORA do updater (chamar invalidate dentro viola a pureza do
  // TanStack e corrompe estado de queries não relacionadas — bug do QR code).
  if (missingAny && onMissing === 'invalidate') {
    bump(s.invalidates, opts.origin);
    log('invalidate', opts.origin, opts.event);
    detalharMotivo(`${opts.origin}:cartao-fora-da-lista`);
    void qc.invalidateQueries({ queryKey: [...keyPrefix] });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Motor de realtime (Fase 2) — encaixe do cartão vindo no evento.
// ─────────────────────────────────────────────────────────────────────────────

/** Quem sou eu, pro juízo de visibilidade local (espelha o servidor). */
export interface CardViewer {
  id: number;
  profile: 'admin' | 'attendant';
  departmentIds: number[];
  /** Sessão de support login — bypass total de dept (igual ao backend). */
  support?: boolean;
}

/** Ordem da lista no servidor (posição 9 da chave). */
type OrdemDaLista = 'atividade' | 'chegada_desc' | 'chegada_asc';

/** Chave da lista:
 *  ['tickets', status, scope, userId, departmentId, q, onlyUnread, onlyUnanswered,
 *   channelId, order]. */
interface ParsedListKey {
  key: readonly unknown[];
  status: 'pending' | 'active' | 'closed';
  scope: 'mine' | 'all';
  /** Filtro "de quem" do admin dentro do Visualizar todos (null = todo mundo). */
  filterUserId: number | null;
  /** Filtro por departamento (null = todos). Qualquer perfil, qualquer aba. */
  filterDeptId: number | null;
  /** Filtro por canal (null = todos). Qualquer perfil, qualquer aba. */
  filterChannelId: number | null;
  q: string;
  filtered: boolean;
  /** 'atividade' (padrão — o card sobe com mensagem) ou ordem de chegada. */
  ordem: OrdemDaLista;
}

function parseListKey(key: readonly unknown[]): ParsedListKey | null {
  if (key[0] !== 'tickets') return null;
  const status = key[1];
  if (status !== 'pending' && status !== 'active' && status !== 'closed') return null;
  const scope = key[2] === 'all' ? 'all' : 'mine';
  const filterUserId = typeof key[3] === 'number' ? key[3] : null;
  const filterDeptId = typeof key[4] === 'number' ? key[4] : null;
  const q = typeof key[5] === 'string' ? key[5] : '';
  const filtered = key[6] === true || key[7] === true;
  const filterChannelId = typeof key[8] === 'number' ? key[8] : null;
  const ordem: OrdemDaLista =
    key[9] === 'chegada_desc' || key[9] === 'chegada_asc' ? key[9] : 'atividade';
  return { key, status, scope, filterUserId, filterDeptId, filterChannelId, q, filtered, ordem };
}

/** Caches que precisam da VERDADE DO SERVIDOR (não dá pra encaixar local):
 *  - busca ativa (só o servidor sabe o que casa com `q`)
 *  - triagem ligada (não lidas / não respondidas — estado que o servidor filtra)
 *
 * `scope: 'all'` NÃO entra mais aqui. Ele ficou de fora do encaixe na primeira
 * versão por precaução, e o custo apareceu na medição: com "Visualizar todos"
 * ligado, cada ação ainda recarregava as 3 abas — ou seja, justo quem usa a
 * visão de gestão não sentia o ganho. Reavaliado: no "todos" quem enxerga é
 * admin (o backend força 'mine' pra atendente), e admin vê tudo — então não há
 * juízo de departamento a fazer, o que torna o caso MAIS simples que o 'mine',
 * não menos. Vide `belongsTo`. */
function needsServerTruth(k: ParsedListKey): boolean {
  return k.q !== '' || k.filtered;
}

/** Lista com busca ou triagem — vide `needsServerTruth`. */
export function listaComBuscaOuTriagem(queryKey: readonly unknown[]): boolean {
  const parsed = parseListKey(queryKey);
  return parsed !== null && needsServerTruth(parsed);
}

/**
 * O cartão pertence a ESTA lista? Espelha os filtros que o `GET /tickets`
 * aplica (`ownerFilter` + `buildDeptAccessCondition` em routes.ts).
 *
 * - status tem que bater sempre.
 * - `scope: 'all'`: só admin/support chega aqui (o backend rebaixa atendente
 *   pra 'mine'), e esse perfil enxerga qualquer departamento → só resta o
 *   filtro opcional "de quem", que compara com o dono do cartão.
 * - `scope: 'mine'`: dono sou eu OU o ticket é órfão (fechado por automação,
 *   sem dono humano — lição #95); pendente não tem dono ainda. Mais o gate de
 *   departamento.
 */
function belongsTo(k: ParsedListKey, card: TicketSummary, me: CardViewer): boolean {
  if (k.status !== card.status) return false;
  // Departamento e canal escolhidos no filtro valem em qualquer aba e escopo
  // (o servidor aplica igual — routes.ts `deptFilter`/`channelFilter`).
  if (k.filterDeptId !== null && card.department_id !== k.filterDeptId) return false;
  if (k.filterChannelId !== null && card.whatsapp_number?.id !== k.filterChannelId) return false;

  if (k.scope === 'all') {
    // Filtro "de quem": escolheu um atendente específico.
    if (k.filterUserId !== null && card.user_id !== k.filterUserId) return false;
    // Perfil de acesso com "Visualizar todos" (não-admin): o servidor mantém o
    // filtro de departamento — o cartão de outro departamento não é desta lista.
    if (
      me.profile !== 'admin' &&
      me.support !== true &&
      card.department_id !== null &&
      !me.departmentIds.includes(card.department_id)
    ) {
      return false;
    }
    return true;
  }

  const deptOk =
    me.profile === 'admin' ||
    me.support === true ||
    card.department_id === null ||
    me.departmentIds.includes(card.department_id);
  const ownerOk = card.status === 'pending' || card.user_id === null || card.user_id === me.id;
  return deptOk && ownerOk;
}

/** Mesma expressão de atividade do ORDER BY do GET /tickets. */
function activityOf(t: TicketSummary): number {
  const last = Date.parse(t.last_message_at);
  if (t.status === 'closed') {
    return t.closed_at ? Date.parse(t.closed_at) : last;
  }
  const tr = t.transferred_at ? Date.parse(t.transferred_at) : 0;
  const re = t.reopened_at ? Date.parse(t.reopened_at) : 0;
  return Math.max(last, tr, re);
}

/**
 * Mesma expressão de CHEGADA do ORDER BY do GET /tickets (order=chegada_*):
 * a mais recente entre abertura, reabertura e transferência. Mensagem não
 * entra — é o que congela a posição. Card sem `created_at` (cache antigo) cai
 * na atividade, pra não quebrar.
 */
function arrivalOf(t: TicketSummary): number {
  if (!t.created_at) return activityOf(t);
  const cr = Date.parse(t.created_at);
  const re = t.reopened_at ? Date.parse(t.reopened_at) : 0;
  const tr = t.transferred_at ? Date.parse(t.transferred_at) : 0;
  return Math.max(cr, re, tr);
}

/**
 * Encaixe numa lista em ORDEM DE CHEGADA (opção da lista, dono 18/out/2026).
 *
 * Diferente da atividade, a posição é congelada: o card que já está na lista
 * e cuja chegada não mudou (chegou mensagem, mudou etiqueta) é atualizado NO
 * LUGAR. Só muda de posição o que mudou de chegada (reabriu, foi transferido)
 * ou entrou agora. A posição é procurada em TODAS as páginas carregadas — com
 * a chegada antiga (ex.: aceitar um pendente) o lugar certo pode ser fundo. Se
 * fica além do que está carregado e há mais páginas, o card não aparece (está
 * na parte ainda não buscada), mas entra no total.
 */
function encaixarPorChegada(
  prev: InfiniteData<TicketsListResponse>,
  cardDoEvento: TicketSummary,
  belongs: boolean,
  crescente: boolean,
): InfiniteData<TicketsListResponse> | null {
  // Card montado a partir de cache antigo (sem `created_at`) herda a data de
  // abertura do item desta lista — senão a chegada viraria a atividade e o
  // card "pularia" na primeira mensagem logo depois do deploy.
  let card = cardDoEvento;
  if (!card.created_at) {
    for (const p of prev.pages) {
      const existente = p?.tickets.find((t) => t.id === card.id);
      if (existente?.created_at) {
        card = { ...card, created_at: existente.created_at };
        break;
      }
    }
  }
  const chegada = arrivalOf(card);

  if (belongs) {
    for (let pi = 0; pi < prev.pages.length; pi++) {
      const page = prev.pages[pi];
      const idx = page ? page.tickets.findIndex((t) => t.id === card.id) : -1;
      if (!page || idx === -1) continue;
      const atual = page.tickets[idx];
      if (atual && arrivalOf(atual) === chegada) {
        const tickets = [...page.tickets];
        tickets[idx] = card;
        return { ...prev, pages: prev.pages.map((p, i) => (i === pi ? { ...page, tickets } : p)) };
      }
      break;
    }
  }

  let removed = 0;
  let pages = prev.pages.map((p) => {
    if (!p) return p;
    const without = p.tickets.filter((t) => t.id !== card.id);
    if (without.length === p.tickets.length) return p;
    removed += p.tickets.length - without.length;
    return { ...p, tickets: without };
  });
  if (!belongs && removed === 0) return null; // nada a fazer neste cache

  if (belongs) {
    // Vem DEPOIS do card na ordem da lista? (decrescente: chegou antes;
    // crescente: chegou depois). Desempate por id, igual ao servidor.
    const vemDepois = (t: TicketSummary): boolean => {
      const a = arrivalOf(t);
      return crescente
        ? a > chegada || (a === chegada && t.id > card.id)
        : a < chegada || (a === chegada && t.id < card.id);
    };
    let alvo: { pi: number; at: number } | null = null;
    for (let pi = 0; pi < pages.length && !alvo; pi++) {
      const page = pages[pi];
      if (!page) continue;
      const at = page.tickets.findIndex(vemDepois);
      if (at !== -1) alvo = { pi, at };
    }
    if (!alvo) {
      // Depois de tudo que está carregado: só cabe se o fim da lista já veio.
      const ultima = pages.length - 1;
      const page = pages[ultima];
      if (page && page.next_cursor == null) alvo = { pi: ultima, at: page.tickets.length };
    }
    if (alvo) {
      const { pi, at } = alvo;
      pages = pages.map((p, i) => {
        if (i !== pi || !p) return p;
        const tickets = [...p.tickets];
        tickets.splice(at, 0, card);
        return { ...p, tickets };
      });
    }
  }

  // `total` (badge da aba) conta o card mesmo se ele caiu na parte não buscada.
  const delta = (belongs ? 1 : 0) - removed;
  if (delta !== 0) {
    pages = pages.map((p, i) => (i === 0 && p ? { ...p, total: Math.max(0, p.total + delta) } : p));
  }
  return { ...prev, pages };
}

/**
 * Encaixa o CARTÃO COMPLETO vindo no evento (`payload.ticket` — Fase 1 do
 * motor) nas listas em cache: remove de onde saiu, insere ordenado onde
 * entrou, ajusta os `total` dos badges. Zero requisição no caminho feliz.
 *
 * Quem decide se o cartão entra em cada lista é `belongsTo`, que espelha os
 * filtros do `GET /tickets` — vale tanto pro modo normal quanto pro
 * "Visualizar todos". (Quem me ENTREGOU o evento já foi a dept room do
 * servidor; o check local resolve o caso do transfer pra dept alheio, onde
 * recebo o evento pela sala ANTIGA só pra remover o cartão.)
 *
 * Caches com busca ou triagem ligada NÃO são tocados — só o servidor sabe o
 * que casa com o filtro; esses são invalidados pontualmente (o refetch só
 * acontece se estiverem na tela). Fallback geral: evento sem cartão → o
 * chamador invalida tudo, como era antes do motor.
 */
export function applyTicketCard(
  qc: QueryClient,
  card: TicketSummary,
  me: CardViewer,
  opts: { origin: string; event?: string },
): void {
  const cardActivity = activityOf(card);

  let changedAny = false;
  const serverTruthKeys: Array<readonly unknown[]> = [];

  for (const query of qc.getQueryCache().findAll({ queryKey: ['tickets'] })) {
    const parsed = parseListKey(query.queryKey);
    if (!parsed) continue;
    if (needsServerTruth(parsed)) {
      serverTruthKeys.push(parsed.key);
      continue;
    }

    const belongs = belongsTo(parsed, card, me);

    qc.setQueryData<InfiniteData<TicketsListResponse>>(parsed.key, (prev) => {
      if (!prev) return prev;
      if (parsed.ordem !== 'atividade') {
        const novo = encaixarPorChegada(prev, card, belongs, parsed.ordem === 'chegada_asc');
        if (!novo) return prev;
        changedAny = true;
        return novo;
      }
      let removed = 0;
      let pages = prev.pages.map((p) => {
        if (!p) return p;
        const without = p.tickets.filter((t) => t.id !== card.id);
        if (without.length === p.tickets.length) return p;
        removed += p.tickets.length - without.length;
        return { ...p, tickets: without };
      });
      if (!belongs && removed === 0) return prev; // nada a fazer neste cache

      if (belongs) {
        const first = pages[0];
        if (!first) return prev;
        // Insere na posição do sort DESC (atividade, id) — mesma ordem do
        // servidor. Cartão mais recente que tudo → topo (caso comum).
        const tickets = [...first.tickets];
        let at = tickets.length;
        for (let i = 0; i < tickets.length; i++) {
          const item = tickets[i];
          if (!item) continue;
          const a = activityOf(item);
          if (a < cardActivity || (a === cardActivity && item.id < card.id)) {
            at = i;
            break;
          }
        }
        tickets.splice(at, 0, card);
        pages = pages.map((p, i) => (i === 0 && p ? { ...p, tickets } : p));
      }

      // `total` alimenta o badge da aba — mantém coerência sem refetch.
      const delta = (belongs ? 1 : 0) - removed;
      if (delta !== 0) {
        pages = pages.map((p, i) =>
          i === 0 && p ? { ...p, total: Math.max(0, p.total + delta) } : p,
        );
      }
      changedAny = true;
      return { ...prev, pages };
    });
  }

  const s = stats();
  bump(s.byEvent, opts.event);
  if (changedAny) {
    bump(s.patches, opts.origin);
    log('patch', opts.origin, opts.event);
  } else {
    bump(s.skips, opts.origin);
    log('skip', opts.origin, opts.event);
  }
  if (serverTruthKeys.length > 0) detalharMotivo(`${opts.origin}:lista-com-busca-ou-filtro`);
  for (const key of serverTruthKeys) {
    bump(s.invalidates, opts.origin);
    void qc.invalidateQueries({ queryKey: [...key] });
  }
}

/** Registra um encaixe (mensagem inserida no cache sem ir ao servidor).
 *  Aparece em `window.__inboxStats.patches` junto com os demais. */
export function recordEncaixe(origin: string, event?: string): void {
  const s = stats();
  bump(s.patches, origin);
  bump(s.byEvent, event);
  log('encaixe', origin, event);
}

/**
 * Acha o cartão de um ticket em QUALQUER lista em cache (1ª ocorrência).
 *
 * É o que permite o eco de `message.received` (que não carrega cartão — os
 * workers não montam TicketSummary) entrar no motor mesmo assim: se o ticket
 * já está em alguma lista, atualizamos o cartão encontrado com os campos do
 * evento e o `applyTicketCard` redistribui — zero requisição. Só quando o
 * ticket não está em lugar nenhum (ticket novo, fora da janela carregada) é
 * que o chamador cai no invalidate.
 */
export function findTicketCard(qc: QueryClient, ticketId: number): TicketSummary | null {
  for (const query of qc.getQueryCache().findAll({ queryKey: ['tickets'] })) {
    const parsed = parseListKey(query.queryKey);
    if (!parsed) continue;
    /**
     * NÃO olha listas de busca/triagem — elas guardam cartão CONGELADO.
     *
     * Era daqui que saía o atendimento que voltava sozinho pra Pendentes.
     * Reproduzido pelo dono:
     *
     *   "Aceitei o atendimento e deixei aberto. Nesse momento eu nao cliquei na
     *    aba ativos, o ticket foi para lá... fiquei fora da aba 1 min +/-, qnd
     *    voltei, ela estava mostrando em pendente."
     *
     * A sequência:
     *
     *   1. em algum momento alguém buscou ou ligou triagem → nasce um cache
     *      tipo ['tickets','pending','mine',null,'texto',false,false], com o
     *      cartão marcado `status: 'pending'`;
     *   2. o motor NUNCA atualiza esses caches — só o servidor sabe o que casa
     *      com o filtro, então eles são invalidados, não remendados. E invalidar
     *      cache INATIVO não busca nada: o cartão congela ali;
     *   3. o atendente aceita → o motor tira o cartão da lista real de
     *      pendentes e põe na de ativos, certinho;
     *   4. chega qualquer `message.received` → `onInboundInbox` pergunta a este
     *      helper "como é o cartão?" e recebia o CONGELADO, com status pendente;
     *   5. o handler monta o cartão novo espalhando o encontrado
     *      (`...found`) — herdando `status: 'pending'` — e o `applyTicketCard`
     *      obedece: recoloca em Pendentes.
     *
     * Por isso era intermitente ("tem hrs q acontece e tem horas q nao"):
     * depende de existir um cache filtrado com aquele atendimento, e da ordem
     * em que o TanStack devolve os caches. Por isso não aparecia no rastro:
     * acontece inteiro em memória, sem uma requisição sequer. E por isso o F5
     * resolvia: recarregar joga fora o cartão congelado.
     *
     * Não veio do delta nem da reconexão — entrou junto com o motor, quando
     * este helper nasceu pra evitar rebuscar lista.
     */
    if (needsServerTruth(parsed)) continue;
    const data = query.state.data as InfiniteData<TicketsListResponse> | undefined;
    if (!data) continue;
    for (const page of data.pages) {
      const hit = page?.tickets.find((t) => t.id === ticketId);
      if (hit) return hit;
    }
  }
  return null;
}

/**
 * "A AtendimentoPage está montada?" — `useRealtimeListeners` registra aqui.
 *
 * O `useDesktopNotifications` (global, AppShell) invalida `ticket-messages`
 * em todo `message.received` como defesa pro caso da página FECHADA (lição
 * #70). Com a página ABERTA isso duplica: `onInboundTicket` já faz append na
 * thread aberta e invalida as fechadas — a defesa global só gerava um
 * refetch extra da conversa a CADA mensagem (medido: 2 GET /messages por
 * envio). Com o contador, a defesa fica restrita a quando é a única cobertura.
 */
export const inboxListeners = { active: 0 };

/**
 * Invalida SÓ os caches de lista que precisam da verdade do servidor (scope
 * 'all', busca, triagem). Usado pelas mutations no lugar do antigo
 * `invalidateQueries(['tickets'])`: o caminho principal (listas mine/pending)
 * é atualizado pelo EVENTO com cartão que o servidor emite pra ação — o autor
 * também o recebe (está nas dept rooms do ticket que acabou de tocar).
 * Exceção coberta aqui: admin agindo em ticket de dept alheio (não recebe o
 * evento) só tem essas visões server-truth pra manter em dia.
 */
export function invalidateServerTruthLists(qc: QueryClient, origin: string): void {
  const s = stats();
  let marcado = false;
  for (const query of qc.getQueryCache().findAll({ queryKey: ['tickets'] })) {
    const parsed = parseListKey(query.queryKey);
    if (!parsed || !needsServerTruth(parsed)) continue;
    bump(s.invalidates, origin);
    if (!marcado) detalharMotivo(`${origin}:lista-com-busca-ou-filtro`);
    marcado = true;
    void qc.invalidateQueries({ queryKey: [...parsed.key] });
  }
}

/**
 * Troca o nome do contato nos cards de TODOS os atendimentos dele.
 *
 * Irmão do `patchTicket`, mas por CONTATO em vez de por atendimento — um
 * contato pode ter vários atendimentos abertos, em canais diferentes, e o nome
 * aparece em todos.
 *
 * Existe porque salvar o contato invalidava as três listas inteiras pra
 * refletir um nome. Aqui o painel já sabe o nome novo: quem digitou foi ele.
 *
 * Não reordena — trocar o nome não muda a posição de nada.
 */
export function patchTicketsDoContato(qc: QueryClient, contactId: number, nomeNovo: string): void {
  qc.setQueriesData<InfiniteData<TicketsListResponse>>({ queryKey: ['tickets'] }, (prev) => {
    if (!prev) return prev;
    let mudou = false;
    const pages = prev.pages.map((p) => {
      if (!p) return p;
      let mudouNaPagina = false;
      const tickets = p.tickets.map((t) => {
        if (t.contact?.id !== contactId || t.contact.display_name === nomeNovo) return t;
        mudouNaPagina = true;
        return { ...t, contact: { ...t.contact, display_name: nomeNovo } };
      });
      if (!mudouNaPagina) return p;
      mudou = true;
      return { ...p, tickets };
    });
    // Mesma referência quando nada mudou — evita re-render à toa (convenção
    // do `patchTicket` logo acima).
    if (!mudou) return prev;
    return { ...prev, pages };
  });
}

/**
 * Troca a FOTO do contato nos cards e no cabeçalho aberto — sem buscar nada.
 *
 * O aviso de foto trocada (canal QR) passou a trazer o link pronto. Antes,
 * sem ele, todo painel que recebia o aviso recarregava as 3 listas (Fechados
 * com todas as páginas) só pra descobrir a foto nova.
 *
 * Só onde a foto exibida é a do QR (atendimento de canal `qr_code`) — a mesma
 * regra do servidor: canal oficial não mostra foto, Instagram usa a dele.
 */
export function trocaFotoDoContato(qc: QueryClient, contactId: number, url: string | null): void {
  const ehDele = (c: { id: number } | undefined, canal: string | undefined): boolean =>
    c?.id === contactId && canal === 'qr_code';
  qc.setQueriesData<InfiniteData<TicketsListResponse>>({ queryKey: ['tickets'] }, (prev) => {
    if (!prev) return prev;
    let mudou = false;
    const pages = prev.pages.map((p) => {
      if (!p) return p;
      let mudouNaPagina = false;
      const tickets = p.tickets.map((t) => {
        if (!ehDele(t.contact, t.whatsapp_number?.channel_type)) return t;
        if (t.contact.profile_pic_url === url) return t;
        mudouNaPagina = true;
        return { ...t, contact: { ...t.contact, profile_pic_url: url } };
      });
      if (!mudouNaPagina) return p;
      mudou = true;
      return { ...p, tickets };
    });
    if (!mudou) return prev;
    return { ...prev, pages };
  });
  qc.setQueriesData<TicketDetails>({ queryKey: ['ticket'] }, (prev) => {
    if (!prev || !ehDele(prev.contact, prev.whatsapp_number?.channel_type)) return prev;
    if (prev.contact.profile_pic_url === url) return prev;
    return { ...prev, contact: { ...prev.contact, profile_pic_url: url } };
  });
}
